import React from "react";
import GlobalDataTable from "../../../global/GlobalDataTable";
import { GlobalTableActionButton } from "../../../global/GlobalTableActionButton";
import { formatInr, getPropertyStatusTone } from "../utils/propertyAssetStats";
import { formatAssetDate, buildPropertyAssetImageUrl } from "../utils/propertyAssetHelpers";
import "../../../shared/styles/VolunteerTable.css";
import "../styles/propertyAssetsTable.css";

/** Directory columns — toggled via Show columns (localStorage). */
export const PROPERTY_ASSET_COLUMN_DEFS = [
  { id: "asset_code", label: "Asset ID", defaultVisible: true, sortable: true },
  { id: "property_asset_name", label: "Asset Name", defaultVisible: true, sortable: true },
  { id: "category_name", label: "Category", defaultVisible: true, sortable: true },
  { id: "quantity", label: "Quantity", defaultVisible: true, sortable: true },
  { id: "brand_model", label: "Brand / Model", defaultVisible: false, sortable: true },
  { id: "purchase_date", label: "Purchase Date", defaultVisible: false, sortable: true },
  { id: "purchase_value", label: "Value", defaultVisible: true, sortable: true },
  { id: "supplier_vendor", label: "Supplier / Vendor", defaultVisible: false, sortable: true },
  { id: "invoice_bill_no", label: "Invoice / Bill No", defaultVisible: false, sortable: true },
  { id: "location_room", label: "Location", defaultVisible: true, sortable: true },
  { id: "condition_name", label: "Condition", defaultVisible: true, sortable: true },
  { id: "status_name", label: "Status", defaultVisible: true, sortable: true },
  { id: "responsible_person_name", label: "Responsible Person", defaultVisible: false, sortable: true },
  { id: "warranty_expiry_date", label: "Warranty Expiry", defaultVisible: false, sortable: true },
  { id: "physical_verification_date", label: "Physical Verification", defaultVisible: false, sortable: true },
  { id: "remarks", label: "Remarks", defaultVisible: false, sortable: true },
  { id: "asset_image_url", label: "Image", defaultVisible: false, sortable: false },
  { id: "created_at", label: "Created", defaultVisible: false, sortable: true },
];

const DATE_COLUMNS = new Set([
  "purchase_date",
  "warranty_expiry_date",
  "physical_verification_date",
  "created_at",
]);

export function getPropertyAssetCellText(columnId, row) {
  switch (columnId) {
    case "purchase_value":
      return formatInr(row.purchase_value);
    case "quantity":
      return row.quantity ?? "—";
    case "asset_image_url":
      return row.asset_image_url ? "Attached" : "—";
    case "remarks": {
      const t = String(row.remarks ?? "").trim();
      if (!t) return "—";
      return t.length > 60 ? `${t.slice(0, 60)}…` : t;
    }
    default:
      if (DATE_COLUMNS.has(columnId)) {
        return formatAssetDate(row[columnId]);
      }
      return row[columnId] ?? "—";
  }
}

/** Full text for view modal (directory column picker controls visibility). */
export function getPropertyAssetViewText(columnId, row) {
  if (columnId === "remarks") {
    const t = String(row?.remarks ?? "").trim();
    return t || "—";
  }
  return getPropertyAssetCellText(columnId, row);
}

function StatusBadge({ name, statusId, statusMasters }) {
  const tone = getPropertyStatusTone(statusId, statusMasters);
  return (
    <span className={`pa-status-badge pa-status-badge--${tone}`}>{name || "—"}</span>
  );
}

function cellValue(columnId, row, statusMasters) {
  if (columnId === "status_name") {
    return (
      <StatusBadge
        name={row.status_name}
        statusId={row.property_status_id}
        statusMasters={statusMasters}
      />
    );
  }
  if (columnId === "purchase_value") {
    return formatInr(row.purchase_value);
  }
  if (columnId === "asset_image_url" && row.asset_image_url) {
    const url = buildPropertyAssetImageUrl(row.asset_image_url);
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "var(--ub-teal-dark, #2c7a7b)", fontWeight: 600 }}
      >
        View
      </a>
    );
  }
  if (columnId === "remarks") {
    const text = String(row.remarks ?? "").trim();
    if (!text) return "—";
    return (
      <span className="pa-remarks-cell" title={text}>
        {text}
      </span>
    );
  }
  return getPropertyAssetCellText(columnId, row);
}

const PropertyAssetsListTable = ({
  records,
  loading,
  columnVisibility,
  statusMasters = [],
  onView,
  onEdit,
  onDelete,
  onPrint,
  canDelete = true,
  canPrint = true,
}) => {
  const visibleCols = PROPERTY_ASSET_COLUMN_DEFS.filter((c) => columnVisibility[c.id]);

  const headers = [
    ...visibleCols.map((c) => ({
      key: c.id,
      label: c.label,
      ...(c.id === "remarks" ? { className: "ub-table__col-remarks" } : {}),
    })),
    { key: "actions", label: "Actions", className: "ub-table__col-actions" },
  ];

  if (loading) return <p className="ub-loading">Loading property assets…</p>;

  return (
    <div className="ub-table-section property-assets-directory-table">
      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Property assets directory"
        headers={headers}
      >
        {records.length === 0 ? (
          <tr>
            <td
              colSpan={Math.max(visibleCols.length + 1, 1)}
              style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
            >
              No property assets found.
            </td>
          </tr>
        ) : (
          records.map((row) => (
            <tr key={row.id}>
              {visibleCols.map((col) => (
                <td
                  key={col.id}
                  className={col.id === "remarks" ? "ub-table__col-remarks" : undefined}
                >
                  {col.id === "asset_code" ? (
                    <span style={{ fontWeight: 500 }}>{cellValue(col.id, row, statusMasters)}</span>
                  ) : (
                    cellValue(col.id, row, statusMasters)
                  )}
                </td>
              ))}
              <td className="ub-table__col-actions">
                <div className="ub-table-actions">
                  {onView ? (
                    <GlobalTableActionButton action="view" onClick={() => onView(row)} />
                  ) : null}
                  {onEdit ? (
                    <GlobalTableActionButton action="edit" onClick={() => onEdit(row)} />
                  ) : null}
                  {canPrint && onPrint ? (
                    <GlobalTableActionButton action="print" onClick={() => onPrint(row)} />
                  ) : null}
                  {canDelete && onDelete ? (
                    <GlobalTableActionButton action="delete" onClick={() => onDelete(row.id)} />
                  ) : null}
                </div>
              </td>
            </tr>
          ))
        )}
      </GlobalDataTable>
    </div>
  );
};

export default PropertyAssetsListTable;
